import { useEffect, useState } from 'react';

type Props = {
  label: string;
  /** Accessible names: the input, and the reset button. */
  inputLabel: string;
  resetLabel: string;
  /** Operator override; null inherits `defaultValue`. */
  value: string | null;
  /** What the pack shows without an override; null when there is none. */
  defaultValue: string | null;
  maxLength: number;
  multiline?: boolean;
  onSave: (value: string) => Promise<void>;
  onReset: () => Promise<void>;
};

/**
 * Labelled, always-visible text setting. Partners missed the hover-only
 * pencils, so the field reads as editable at all times; it saves when focus
 * leaves (or on Enter for one-line fields) and says when it differs from the
 * pack's default.
 */
export function TextSetting({
  label,
  inputLabel,
  resetLabel,
  value,
  defaultValue,
  maxLength,
  multiline = false,
  onSave,
  onReset,
}: Props) {
  const isCustom = Boolean(value);
  const shown = value || defaultValue || '';
  const [draft, setDraft] = useState(shown);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(shown), [shown]);

  async function run(action: () => Promise<void>) {
    setSaving(true);
    try {
      await action();
    } finally {
      setSaving(false);
    }
  }

  function commit() {
    const next = draft.trim();
    if (next === shown) return;
    // Clearing the field means "use the default again".
    if (next === '' || next === defaultValue) {
      if (isCustom) void run(onReset);
      else setDraft(shown);
      return;
    }
    void run(() => onSave(next));
  }

  const fieldClass =
    'w-full rounded border border-[var(--co-border)] bg-[var(--co-bg)] px-2 py-1.5 text-[13px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)] disabled:opacity-60';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-medium text-[var(--co-text)]">{label}</span>
        {isCustom && (
          <>
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
              custom
            </span>
            <button
              type="button"
              onClick={() => void run(onReset)}
              disabled={saving}
              aria-label={resetLabel}
              className="text-[11px] text-[var(--co-muted)] underline-offset-2 hover:text-[var(--co-text)] hover:underline"
            >
              Reset to default
            </button>
          </>
        )}
      </div>

      {multiline ? (
        <textarea
          rows={3}
          value={draft}
          maxLength={maxLength}
          disabled={saving}
          placeholder={defaultValue ?? ''}
          aria-label={inputLabel}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setDraft(shown);
          }}
          className={`${fieldClass} resize-y leading-snug`}
        />
      ) : (
        <input
          type="text"
          value={draft}
          maxLength={maxLength}
          disabled={saving}
          placeholder={defaultValue ?? ''}
          aria-label={inputLabel}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setDraft(shown);
          }}
          className={fieldClass}
        />
      )}

      {isCustom && defaultValue && (
        <p className="truncate text-[11px] text-[var(--co-muted)]" title={defaultValue}>
          Default: {defaultValue}
        </p>
      )}
    </div>
  );
}
